import type { ReactNode, RefObject } from 'react';
import { memo, useEffect, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';
import useResizeObserver from 'use-resize-observer/polyfilled';
import { TypographyVariant } from '../../utils/theme';
import type { ITooltipProps } from '../tooltip/tooltip';
import { Tooltip } from '../tooltip/tooltip';
import type { TypographyProps } from '../typography/typography';
import Typography from '../typography/typography';

export interface ITruncatedTextProps extends TypographyProps {
  children?: ReactNode;
  lineClamp?: number;
  variant?: TypographyVariant;
  tooltipText?: string;
  tooltipProps?: Partial<ITooltipProps>;
  onOverflowChange?: (isOverflowing: boolean) => void;
  showTooltip?: boolean;
}

const TruncatedText = ({
  children,
  lineClamp,
  variant = TypographyVariant.bodyMd1,
  tooltipText,
  tooltipProps,
  onOverflowChange,
  showTooltip = true,
  as,
  ...typographyProps
}: ITruncatedTextProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { height: containerHeight, width: containerWidth } =
    useResizeObserver<HTMLDivElement>({
      ref: wrapperRef as RefObject<HTMLDivElement>,
    });

  const isOverflowing = useMemo(() => {
    if (!wrapperRef.current || !containerHeight || !containerWidth) {
      return false;
    }
    if (lineClamp) {
      const { scrollHeight } = wrapperRef.current;
      return scrollHeight > containerHeight;
    } else {
      const { scrollWidth } = wrapperRef.current;
      return scrollWidth > containerWidth;
    }
  }, [lineClamp, containerHeight, containerWidth]);

  useEffect(() => {
    onOverflowChange?.(isOverflowing);
  }, [isOverflowing, onOverflowChange]);

  return (
    <Tooltip
      tooltipText={tooltipText ?? children}
      hideTooltip={!isOverflowing || !showTooltip}
      noAnchorRole
      tabIndex={-1}
      tooltipAnchorStyle={{ width: '100%' }}
      {...tooltipProps}
    >
      <StyledTypography
        ref={wrapperRef}
        variant={variant}
        lineClamp={lineClamp}
        forwardedAs={as}
        {...typographyProps}
      >
        {children}
      </StyledTypography>
    </Tooltip>
  );
};

export default memo(TruncatedText);

const StyledTypography = styled(Typography)`
  ${({ lineClamp }: { lineClamp?: number }) => css`
    overflow: hidden;

    ${lineClamp === undefined
      ? `
      text-overflow: ellipsis;
      white-space: nowrap;
    `
      : `
      display: -webkit-box;
      -webkit-line-clamp: ${lineClamp};
      -webkit-box-orient: vertical;
      overflow-wrap: anywhere;
      hyphens: auto;
    `}
  `}
`;
